import SportFilter from "../../../../admin/admincomponent/sportFilter/SportFilter";
import React, { useState, useContext, useEffect } from "react";
import { Badge, Button, Card, Col, Row, Alert } from "react-bootstrap";
import swal from "sweetalert";
import DistrictFilter from "../../../../admin/admincomponent/districtFilter/DistrictFilter";
import { LocationContext } from "../../../../context/LocationContext";
import {
  getInstitutionList,
  getUserCategoryTypes,
  getKitSizeList
} from "../../../../services/userApiService/userService";

const fallbackImg =
  "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

export default function EditSections({
  userData,
  userProfileImage,
  sections,
  onAddSection,
  onRemoveSection,
  onFieldChange,
  onValueChange,
  onFileChange,
  onSubmit,
  isSubmitting,
  onPhotoFileChange,
  validationErrors = {},
  clearValidationError = () => { },
  categoryType,
  onRemoveFile,
  userCategoryMain
}) {

  const [generalError, setGeneralError] = useState("");
  const { districtList } = useContext(LocationContext);

  // Updated Logic based on requirements:
  // Athlete: category_type = 1 & user_category_main = 1
  // Support Staff: category_type = 2 & user_category_main = 1
  // Institution: user_category_main = 2

  const isAthlete = String(categoryType) === '1' && String(userCategoryMain) === '1';
  const isSupportStaff = String(categoryType) === '2' && String(userCategoryMain) === '1';
  const isInstitution = String(userCategoryMain) === '2';

  const [centerTypes, setCenterTypes] = useState([]);
  const [centerList, setCenterList] = useState([]);
  const [kitSizeList, setKitSizeList] = useState([]);

  useEffect(() => {
    getKitSizeList()
      .then((res) => {
        if (res?.data?.status) {
          setKitSizeList(res.data.data || []);
        }
      })
      .catch((err) => console.error("Failed to fetch kit sizes", err));
  }, []);

  useEffect(() => {
    if (isAthlete || isSupportStaff || isInstitution) {
      getInstitutionList()
        .then((response) => {
          // Ensure we extract the array correctly based on standard API wrappers
          const data = response?.data?.data || [];
          // Assuming the list items have 'category_type' property to use as value/label if 'center_name' is missing
          // But based on request, we are fetching "UserCategoryType". 
          // It likely returns object like { id: 1, type_name: "..." } or similar.
          // I will use a generic map to be safe: 'id' and 'category_name' or 'name'.
          // However, the previous code used 'center_name'. 
          // The API name is findUserCategoryType... so likely returns 'category_name'.
          // I'll stick to a robust fallback usage.
          setCenterTypes(Array.isArray(data) ? data : []);
        })
        .catch((error) => {
          console.error("Failed to fetch institution list", error);
        });
    }
  }, [categoryType, isAthlete, isInstitution]);

  // Fetch Center List when District and Center Type are selected
  // We extract the relevant values to avoid depending on the entire 'sections' array
  const centerDetailsSection = sections.find(s => s.field === "center_details");
  const rawDist = centerDetailsSection?.values?.district;
  const rawType = centerDetailsSection?.values?.center_type;

  // Stable primitive dependencies prevents infinite loops
  const districtValue = rawDist ? String(rawDist) : "";
  const centerTypeValue = rawType ? String(rawType) : "";

  useEffect(() => {
    // Only for non-institution users
    if (!isInstitution && districtValue && centerTypeValue) {
      getUserCategoryTypes(2, centerTypeValue, districtValue)
        .then((res) => {
          const data = res?.data?.data || [];
          const list = Array.isArray(data) ? data : [data].filter(Boolean);
          setCenterList(list);
        })
        .catch(err => {
          console.error("Failed to fetch center list", err);
          setCenterList([]);
        });
    } else {
      if (!districtValue || !centerTypeValue) {
        setCenterList([]);
      }
    }
  }, [districtValue, centerTypeValue, isInstitution]);

  // Track synced values to prevent infinite update loops
  const lastSynced = React.useRef({});

  // Initialize/Sync Names for existing values (e.g. from userData)
  useEffect(() => {
    sections.forEach(searchSection => {
      // Sync Kit Name
      if (searchSection.field === "kit_size" && searchSection.values.kit_size) {
        const val = String(searchSection.values.kit_size);
        const currentName = searchSection.values.kit_name;
        const key = `${searchSection.id}_kit_${val}`;

        if (kitSizeList.length > 0) {
          const selectedKit = kitSizeList.find(k => String(k.id) === val);
          if (selectedKit) {
            const label = selectedKit.size_name;
            if (label && currentName !== label && lastSynced.current[key] !== label) {
              onValueChange(searchSection.id, "kit_name", label);
              lastSynced.current[key] = label;
            }
          }
        }
      }

      // Sync Center Type Name and District Name
      if (searchSection.field === "center_details") {
        const vals = searchSection.values || {};

        // District Name
        if (vals.district && districtList?.length > 0) {
          const val = String(vals.district);
          const currentName = vals.district_name;
          const key = `${searchSection.id}_dist_${val}`;

          const selectedDist = districtList.find(d => String(d.id) === val);
          if (selectedDist) {
            const label = selectedDist.district_name;
            if (label && currentName !== label && lastSynced.current[key] !== label) {
              onValueChange(searchSection.id, "district_name", label);
              lastSynced.current[key] = label;
            }
          }
        }

        // Center Type Name
        if (vals.center_type) {
          const val = String(vals.center_type);
          const currentName = vals.center_type_name;
          const key = `${searchSection.id}_type_${val}`;
          let label = "";

          if (centerTypes.length > 0) {
            const selectedType = centerTypes.find(t => String(t.id) === val);
            if (selectedType) {
              label = selectedType.category_name || selectedType.center_name || selectedType.name || selectedType.type_name || "Unknown";
            }
          }

          if (!label) {
            if (val === "KRIDA KISLAYA") label = "KRIDA KISLAYA";
            else if (val === "OTHER") label = "OTHER";
          }

          if (label && currentName !== label && lastSynced.current[key] !== label) {
            onValueChange(searchSection.id, "center_type_name", label);
            lastSynced.current[key] = label;
          }
        }

        // Center Name Text
        if (vals.center_name && centerList.length > 0) {
          const val = String(vals.center_name);
          const currentName = vals.center_name_text;
          const key = `${searchSection.id}_cname_${val}`;

          const selectedCenter = centerList.find(c => String(c.id) === val);
          if (selectedCenter) {
            const label = selectedCenter?.name_of_legel_entity || selectedCenter?.organization_profile?.name_of_organization || selectedCenter?.name || "Unknown Center";
            if (label && currentName !== label && lastSynced.current[key] !== label) {
              onValueChange(searchSection.id, "center_name_text", label);
              lastSynced.current[key] = label;
            }
          }
        }
      }
    });
  }, [sections, kitSizeList, centerTypes, centerList, districtList]);

  let availableFields = [
    { value: "name", label: "Name" },
    { value: "dob", label: "Date Of Birth" },
    { value: "address", label: "Address" },
    { value: "profile", label: "Profile Image" },
    { value: "kit_size", label: "Kit Size" },
    { value: "bank_details", label: "Bank Details" },
    { value: "educational_details", label: "Education Details" },
    { value: "center_details", label: "Centre Details" },
    { value: "identification", label: "Identification" },
    { value: "sports_achievement", label: "Sports Achievement" },
  ];



  if (isSupportStaff) {
    availableFields = availableFields.filter(f => f.value !== "kit_size" && f.value !== "sports_achievement");
    availableFields.push(
      { value: "sport", label: "Sport" },
      { value: "marital_status", label: "Marital Status" }
    );
  } else if (isInstitution) {
    availableFields = [
      { value: "center_name", label: "Center Name" },
      { value: "contact_person", label: "Contact Person Name" },
      { value: "center_type", label: "Center Type" },
      { value: "center_district", label: "Center District" },
    ];
  }

  const educationLevels = [
    { label: "10th Standard", key: "tenth" },
    { label: "12th Standard", key: "twelfth" },
    { label: "Under Graduate", key: "ug" },
    { label: "Post Graduate", key: "pg" },
    { label: "PhD", key: "phd" },
    { label: "TRANSFER CERTIFICATE", key: "tc" },
  ];

  const getFieldErrors = (id, keywords = []) => {
    const msgs = (validationErrors?.[id]?.messages) || [];
    if (!keywords.length) return msgs;
    return msgs.filter((m) =>
      keywords.some((k) => m.toLowerCase().includes(k.toLowerCase()))
    );
  };

  const handleRemoveFile = (sectionId, subType = null) => {
    // console.log(sectionId, subType);
    if (onRemoveFile) {
      onRemoveFile(sectionId, subType);
    } else {
      // Fallback if prop not provided (though it should be)
      if (subType) {
        const section = sections.find(s => s.id === sectionId);
        if (section && section.values.education_uploads) {
          const newUploads = { ...section.values.education_uploads };
          delete newUploads[subType];
          onValueChange(sectionId, "education_uploads", newUploads);
        }
      } else {
        onValueChange(sectionId, "fileUrl", "");
        onValueChange(sectionId, "file", null);
        onValueChange(sectionId, "documentId", null);
      }
    }
  };

  const handleAddSection = () => {
    // Clear previous general error
    setGeneralError("");

    // Check if any section has no field selected
    const hasEmptyField = sections.some(section => !section.field);

    if (hasEmptyField) {
      setGeneralError("Please select a field for all sections before adding a new one.");
      // swal("Error", "Please select a field for all sections before adding a new one.", "error");
      // Scroll to top to show error
      // window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // If all sections have fields selected, add new section
    onAddSection();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Clear previous general error
    setGeneralError("");

    // Check if at least one field is selected
    const hasSelectedField = sections.some(section => section.field);

    if (!hasSelectedField) {
      setGeneralError("Please select at least one field to update before submitting.");
      // swal("Error", "Please select at least one field to update before submitting.", "error");
      // Scroll to top to show error
      // window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Check if any selected section is incomplete
    const hasEmptyFieldInSelected = sections.some(section => section.field && !section.field);

    if (hasEmptyFieldInSelected) {
      setGeneralError("Please complete all selected fields before submitting.");
      // swal("Error", "Please complete all selected fields before submitting.", "error");
      // window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

  // Validation: disallow all-zero bank account numbers
  const hasAllZeroAccountNumber = sections.some((section) =>
    section.field === "bank_details" &&
    section.values?.account_number &&
    /^0+$/.test(section.values.account_number)
  );

  if (hasAllZeroAccountNumber) {
    setGeneralError("Account number cannot be all zeros.");
    return;
  }

    // If validation passes, call original onSubmit
    onSubmit(e);
  };

  console.log(sections);

  return (
    <Card className="p-4 mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="user-header mb-0">Edit Personal Details</h5>
          <Badge bg="light" text="success">
            Updated
          </Badge>
        </div>

        {/* General Error Alert */}
        {generalError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setGeneralError("")}
            className="mb-3"
          >
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {generalError}
          </Alert>
        )}

        <form onSubmit={handleFormSubmit}>
          {sections.map((section, idx) => {
            const selectedFields = sections.map((s) => s.field).filter(Boolean);
            const filteredFields = availableFields.filter(
              (item) => !selectedFields.includes(item.value) || item.value === section.field
            );

            return (
              <div key={section.id} className="border rounded p-3 mb-3" style={{ position: "relative" }}>
                {(idx !== 0 || section.field) && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      // Clear general error when removing/resetting section
                      setGeneralError("");

                      if (idx === 0) {
                        onFieldChange(section.id, "");
                        Object.keys(section.values).forEach(key => {
                          onValueChange(section.id, key, "");
                        });
                        clearValidationError(section.id);
                      } else {
                        onRemoveSection(section.id);
                      }
                    }}
                    className="remove-btn"
                  >
                    Remove Details <i className="bi bi-x-circle ms-1"></i>
                  </Button>
                )}

                <Row className="g-3">
                  <Col md={6}>
                    <label className="edit-form-label">Select Field</label>
                    <select
                      className="form-select bg-white w-50 p-2"
                      value={section.field}
                      onChange={(e) => {
                        // Clear general error when selecting a field
                        setGeneralError("");
                        onFieldChange(section.id, e.target.value);
                        clearValidationError(section.id);
                      }}
                    >
                      <option value="" disabled>Select Here</option>
                      {filteredFields.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>
                    <div className="edit-form-text">Choose the detail you want to update</div>
                  </Col>

                  {section.field === "name" && (
                    <>
                      <Col xs={12} />
                      <Col md={4}>
                        <label className="form-label">First Name <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          type="text"
                          value={section.values.first_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "first_name", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="First Name"
                          minLength={3}
                        />
                        {section.values.first_name && section.values.first_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["first name", "first_name"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["first name", "first_name"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Middle Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={section.values.middle_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "middle_name", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Middle Name"
                          minLength={3}
                        />
                        {section.values.middle_name && section.values.middle_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Last Name</label>
                        <input
                          className="form-control"
                          value={section.values.last_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "last_name", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Last Name"
                          minLength={3}
                        />
                        {section.values.last_name && section.values.last_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["last name", "last_name"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["last name", "last_name"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "dob" && (
                    <>
                      <Col xs={12} />
                      <Col md={4}>
                        <label className="form-label">DOB <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={section.values.dob}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) => { onValueChange(section.id, "dob", e.target.value); clearValidationError(section.id); }}
                          onKeyDown={(e) => e.preventDefault()} // Prevent manual typing if desired, or let it be
                        />
                        {getFieldErrors(section.id, ["dob"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["dob"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "address" && (
                    <>
                      <Col xs={12} />
                      <Col md={4}>
                        <label className="form-label">District</label>
                        <DistrictFilter
                          value={section.values.district || section.values.city || ''}
                          onChange={(val, distName) => {
                            onValueChange(section.id, "district", val);
                            if (distName) {
                              onValueChange(section.id, "district_name", distName);
                            }
                            clearValidationError(section.id);
                          }}
                          placeholder="Select District"
                        />
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Pincode<span className="text-danger">*</span></label>
                        <input
                          type="number"
                          className="form-control"
                          value={section?.values?.pincode}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val.length <= 6) {
                              onValueChange(section.id, "pincode", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Pincode"
                        />
                        {section.values.pincode && !/^[1-9][0-9]{5}$/.test(section.values.pincode) && (
                          <div className="text-danger small mt-1">
                            Invalid Pincode (Must be 6 digits, can't start with 0).
                          </div>
                        )}
                        {getFieldErrors(section.id, ["pincode"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["pincode"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Landmark</label>
                        <input
                          className="form-control"
                          value={section.values.landmark}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Support Staff: only letters and spaces, Athletes: alphanumeric
                            const regex = isSupportStaff ? /^[a-zA-Z0-9\s]*$/ : /^[a-zA-Z0-9\s]*$/;
                            if (regex.test(val)) {
                              onValueChange(section.id, "landmark", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Landmark"
                          minLength={3}
                        />
                        {section.values.landmark && section.values.landmark.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["landmark"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["landmark"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={6}>
                        <label className="form-label">Full Address</label>
                        <input
                          className="form-control"
                          value={section.values.address}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z0-9\s]*$/.test(val)) {
                              onValueChange(section.id, "address", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Enter Full Address"
                          minLength={3}
                        />
                        {section.values.address && section.values.address.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["address", "full address"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["address", "full address"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "profile" && (
                    <>
                      <Col xs={12}>
                        <label className="form-label">Previous Photo</label>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: 8,
                              border: "1px solid #E5E7EB",
                              background: "#F9FAFB",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            {(section.values.photofileUrl || userProfileImage) ? (
                              <img
                                src={section.values.photofileUrl || userProfileImage}
                                alt="uploaded preview"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <span className="text-muted"><i className="bi bi-image fs-4"></i></span>
                            )}
                          </div>
                        </div>
                      </Col>
                      {getFieldErrors(section.id, ["profile", "supportive", "document"]).length > 0 && (
                        <Col xs={12}>
                          <div className="text-danger small mt-1">
                            {getFieldErrors(section.id, ["profile", "supportive", "document"]).join('. ')}
                          </div>
                        </Col>
                      )}
                    </>
                  )}

                  {section.field === "kit_size" && (
                    <>
                      <Col xs={12} />
                      <Col md={4}>
                        <label className="form-label">Kit Size <span className="text-danger">*</span></label>
                        <select
                          className="form-select bg-white"
                          value={section.values.kit_size || ""}
                          onChange={(e) => {
                            onValueChange(section.id, "kit_size", e.target.value);
                            const selectedKit = kitSizeList.find(k => String(k.id) === String(e.target.value));
                            if (selectedKit) {
                              onValueChange(section.id, "kit_name", selectedKit.size_name);
                            }
                            clearValidationError(section.id);
                          }}
                        >
                          <option value="" disabled>Select Kit Size</option>
                          {kitSizeList.map((size) => (
                            <option key={size.id} value={size.id}>{size.size_name}</option>
                          ))}
                        </select>
                        {getFieldErrors(section.id, ["kit size", "kit_size", "kit"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["kit size", "kit_size", "kit"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "sport" && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Sport<span className="text-danger">*</span></label>
                        {console.log(section.values.sport)}
                        <SportFilter
                          value={section.values.sport || ''}
                          onChange={(val, sportName) => {
                            onValueChange(section.id, "sport", val);
                            if (sportName) {
                              onValueChange(section.id, "sport_name", sportName);
                            }
                            clearValidationError(section.id);
                          }}
                        />
                        {getFieldErrors(section.id, ["sport"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["sport"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "marital_status" && (
                    <>
                      <Col xs={12} />
                      <Col md={3}>
                        <label className="form-label">Marital Status<span className="text-danger">*</span></label>
                        <select
                          className="form-select bg-white"
                          value={section.values.marital_status || ""}
                          onChange={(e) => {
                            onValueChange(section.id, "marital_status", e.target.value);
                            clearValidationError(section.id);
                          }}
                        >
                          <option value="" disabled>Select Status</option>
                          <option value="Unmarried">Unmarried</option>
                          <option value="Married">Married</option>
                        </select>
                        {getFieldErrors(section.id, ["marital", "marital_status"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["marital", "marital_status"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "bank_details" && (
                    <>
                      <Col xs={12} />
                      <Col md={4}>
                        <label className="form-label">Bank Name<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.bank_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "bank_name", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Bank Name"
                          minLength={3}
                        />
                        {section.values.bank_name && section.values.bank_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["bank name", "bank_name"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["bank name", "bank_name"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Account Holder Name<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.account_holder_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "account_holder_name", val.toUpperCase());
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Account Holder Name"
                          minLength={3}
                        />
                        {section.values.account_holder_name && section.values.account_holder_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["account holder", "account_holder_name"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["account holder", "account_holder_name"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Account Number<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          type="text"
                          value={section.values.account_number}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[0-9]*$/.test(val)) {
                              onValueChange(section.id, "account_number", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Account Number"
                          minLength={9}
                          maxLength={18}
                        />
                        {section.values.account_number && /^0+$/.test(section.values.account_number) && (
                          <div className="text-danger small mt-1">Account number cannot be all zeros</div>
                        )}
                        {section.values.account_number && section.values.account_number.length < 9 && (
                          <div className="text-danger small mt-1">Min 9 digits required</div>
                        )}
                        {getFieldErrors(section.id, ["account number", "account_number"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["account number", "account_number"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">Branch Name<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.branch_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "branch_name", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Branch Name"
                          minLength={3}
                        />
                        {section.values.branch_name && section.values.branch_name.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["branch name", "branch_name"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["branch name", "branch_name"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={4}>
                        <label className="form-label">IFSC Code<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.ifsc_code}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z0-9]*$/.test(val)) {
                              onValueChange(section.id, "ifsc_code", val.toUpperCase());
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="IFSC Code"
                          maxLength={11}
                        />
                        {section.values.ifsc_code && section.values.ifsc_code.length !== 11 && (
                          <div className="text-danger small mt-1">Must be exactly 11 characters</div>
                        )}
                        {getFieldErrors(section.id, ["ifsc", "ifsc_code"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["ifsc", "ifsc_code"]).join('. ')}</div>
                        )}
                      </Col>
                    </>

                  )}

                  {section.field === "educational_details" && (
                    <Col xs={12}>
                      <Row className="mb-3">
                        <Col md={3}>
                          <label className="form-label">Highest Qualification <span className="text-danger">*</span></label>
                          <select
                            className="form-select bg-white"
                            value={section.values.highest_qualification || ""}
                            onChange={(e) => {
                              onValueChange(section.id, "highest_qualification", e.target.value);
                            }}
                          >
                            <option value="" disabled>Select Qualification</option>
                            {educationLevels.map((level) => (
                              <option key={level.key} value={level.key}>{level.label}</option>
                            ))}
                          </select>
                          {getFieldErrors(section.id, ["Highest Qualification", "highest_qualification"]).length > 0 && (
                            <div className="text-danger small mt-1">{getFieldErrors(section.id, ["Highest Qualification", "highest_qualification"]).join('. ')}</div>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  )}

                  {section.field === "center_details" && (
                    <>
                      <Col xs={12}>
                        <h6 className="text-danger small mb-3">Selecting one of the category is compulsory</h6>
                      </Col>
                      <Col md={4}>
                        {console.log({ section })}
                        <label className="form-label">Select District <span className="text-danger">*</span></label>
                        <DistrictFilter
                          value={section.values.district || ''}
                          onChange={(val, distName) => {
                            onValueChange(section.id, "district", val);
                            if (distName) {
                              onValueChange(section.id, "district_name", distName);
                            }
                            // Reset dependent fields
                            onValueChange(section.id, "center_name", "");
                            clearValidationError(section.id);
                          }}
                          placeholder="Select District"
                        />
                      </Col>
                      {(isAthlete || isSupportStaff) && (
                        <>
                          <Col md={4}>
                            <label className="form-label">Select Center Type <span className="text-danger">*</span></label>
                            <select
                              className="form-select bg-white"
                              value={section.values.center_type || ""}
                              onChange={(e) => {
                                onValueChange(section.id, "center_type", e.target.value);
                                // Find selected type name
                                const selectedType = centerTypes.find(t => String(t.id) === String(e.target.value));
                                if (selectedType) {
                                  // As per parsing logic below: category_name || center_name || name || type_name
                                  const label = selectedType.category_name || selectedType.center_name || selectedType.name || selectedType.type_name || "Unknown";
                                  onValueChange(section.id, "center_type_name", label);
                                } else if (e.target.value === "KRIDA KISLAYA") {
                                  onValueChange(section.id, "center_type_name", "KRIDA KISLAYA");
                                } else if (e.target.value === "OTHER") {
                                  onValueChange(section.id, "center_type_name", "OTHER");
                                }

                                // Reset dependent fields
                                onValueChange(section.id, "center_name", "");
                                onValueChange(section.id, "center_name_text", "");
                                clearValidationError(section.id);
                              }}
                            >
                              <option value="" disabled>Select Center Type</option>
                              {(isAthlete || isSupportStaff) && centerTypes.length > 0 ? (
                                centerTypes.map((item, index) => {
                                  // Try to resolve the label and value dynamically based on common fields
                                  // The API is findUserCategoryType... so likely 'category_name' or 'type_name'
                                  const label = item.category_name || item.center_name || item.name || item.type_name || "Unknown";
                                  const value = item.id; // Use ID as value
                                  return (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  );
                                })
                              ) : (
                                <>
                                  <option value="KRIDA KISLAYA">KRIDA KISLAYA</option>
                                  <option value="OTHER">OTHER</option>
                                </>
                              )}
                            </select>
                          </Col>
                          {getFieldErrors(section.id, ["Center Type"]).length > 0 && (
                            <div className="text-danger small mt-1 ms-3">
                              {getFieldErrors(section.id, ["Center Type"]).join('. ')}
                            </div>
                          )}

                          {section.values.district && section.values.center_type && (
                            <Col md={4}>
                              <label className="form-label">Select Center Name <span className="text-danger">*</span></label>
                              <select
                                className="form-select bg-white"
                                value={section.values.center_name || ""}
                                onChange={(e) => {
                                  onValueChange(section.id, "center_name", e.target.value);
                                  const selectedCenter = centerList.find(c => String(c.id) === String(e.target.value));
                                  if (selectedCenter) {
                                    const label = selectedCenter?.name_of_legel_entity || selectedCenter?.organization_profile?.name_of_organization || selectedCenter?.name || "Unknown Center";
                                    onValueChange(section.id, "center_name_text", label);
                                  }
                                  clearValidationError(section.id);
                                }}
                              >
                                <option value="">Select an option</option>
                                {centerList.map((center) => {
                                  // User requested: "response of api comes under the field is getting is name_of_legal_entity"
                                  const label = center?.name_of_legel_entity || center?.organization_profile?.name_of_organization || center?.name || "Unknown Center";

                                  // User wants organization_id (which looks like the user ID of the center) as value
                                  return (
                                    <option key={center.id} value={center.id}>{label}</option>
                                  );
                                })}
                              </select>
                              {getFieldErrors(section.id, ["Center Name"]).length > 0 && (
                                <div className="text-danger small mt-1 ms-3">
                                  {getFieldErrors(section.id, ["Center Name"]).join('. ')}
                                </div>
                              )}
                            </Col>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {section.field === "center_name" && isInstitution && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Center Name <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.center_name || ""}
                          onChange={(e) => {
                            onValueChange(section.id, "center_name", e.target.value);
                            clearValidationError(section.id);
                          }}
                          placeholder="Center Name"
                          minLength={3}
                        />
                      </Col>
                    </>
                  )}

                  {section.field === "contact_person" && isInstitution && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Center Contact Person Name <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.contact_person || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-Z\s]*$/.test(val)) {
                              onValueChange(section.id, "contact_person", val);
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder="Contact Person Name"
                          minLength={3}
                        />
                        {section.values.contact_person && section.values.contact_person.length < 3 && (
                          <div className="text-danger small mt-1">Min 3 characters required</div>
                        )}
                        {getFieldErrors(section.id, ["Contact Person"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["Contact Person"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "center_type" && isInstitution && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Select Center Type <span className="text-danger">*</span></label>
                        <select
                          className="form-select bg-white"
                          value={section.values.center_type || ""}
                          onChange={(e) => {
                            onValueChange(section.id, "center_type", e.target.value);
                            const selectedType = centerTypes.find(t => String(t.id) === String(e.target.value));
                            if (selectedType) {
                              const label = selectedType.category_name || selectedType.center_name || selectedType.name || selectedType.type_name || "Unknown";
                              onValueChange(section.id, "center_type_name", label);
                            } else if (e.target.value === "KRIDA KISLAYA") {
                              onValueChange(section.id, "center_type_name", "KRIDA KISLAYA");
                            } else if (e.target.value === "OTHER") {
                              onValueChange(section.id, "center_type_name", "OTHER");
                            }
                            clearValidationError(section.id);
                          }}
                        >
                          <option value="" disabled>Select Center Type</option>
                          {centerTypes.length > 0 ? (
                            centerTypes.map((item) => {
                              const label = item.category_name || item.center_name || item.name || item.type_name || "Unknown";
                              return (
                                <option key={item.id} value={item.id}>{label}</option>
                              );
                            })
                          ) : (
                            <>
                              <option value="KRIDA KISLAYA">KRIDA KISLAYA</option>
                              <option value="OTHER">OTHER</option>
                            </>
                          )}
                        </select>
                        {getFieldErrors(section.id, ["Center Type"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["Center Type"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "center_district" && isInstitution && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Select Center District <span className="text-danger">*</span></label>
                        <DistrictFilter
                          value={section.values.district || ""}
                          onChange={(val, distName) => {
                            onValueChange(section.id, "district", val);
                            if (distName) {
                              onValueChange(section.id, "district_name", distName);
                            }
                            clearValidationError(section.id);
                          }}
                          placeholder="Select District"
                        />
                        {getFieldErrors(section.id, ["district", "Center District"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["district", "Center District"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field === "identification" && (
                    <>
                      <Col xs={12} />
                      <Col md={6}>
                        <label className="form-label">Identification Type<span className="text-danger">*</span></label>
                        <select
                          className="form-select bg-white"
                          value={section.values.identification_type || ""}
                          onChange={(e) => {
                            onValueChange(section.id, "identification_type", e.target.value);
                            // Clear identification number when type changes
                            onValueChange(section.id, "identification_number", "");
                            clearValidationError(section.id);
                          }}
                        >
                          <option value="" disabled>Select Type</option>
                          <option value="aadhaar">Aadhaar Card</option>
                          <option value="pan_card">PAN Card</option>
                          <option value="driving_licence">Driving Licence</option>
                          <option value="passport">Passport</option>
                          <option value="birth_certificate">Birth Certificate</option>
                          <option value="voter_id">Voter ID</option>
                          <option value="tenth_identity">10th Marksheet</option>
                        </select>
                        {getFieldErrors(section.id, ["identification type", "identification_type"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["identification type", "identification_type"]).join('. ')}</div>
                        )}
                      </Col>
                      <Col md={6}>
                        <label className="form-label">Identification Number<span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={section.values.identification_number}
                          onChange={(e) => {
                            const val = e.target.value;
                            const idType = section.values.identification_type;

                            // Apply validation based on identification type
                            let isValid = true;

                            if (idType === "aadhaar") {
                              // Aadhaar: 12 digits only
                              isValid = /^\d{0,12}$/.test(val);
                            } else if (idType === "pan_card") {
                              // PAN: 10 alphanumeric characters (format: ABCDE1234F)
                              isValid = /^[A-Z0-9]{0,10}$/i.test(val);
                            } else if (idType === "driving_licence") {
                              // Driving License: Alphanumeric, typically 16 characters
                              isValid = /^[A-Z0-9]{0,16}$/i.test(val);
                            } else if (idType === "passport") {
                              // Passport: 8 alphanumeric characters
                              isValid = /^[A-Z0-9]{0,8}$/i.test(val);
                            } else if (idType === "voter_id") {
                              // Voter ID: 10 alphanumeric characters
                              isValid = /^[A-Z0-9]{0,10}$/i.test(val);
                            } else {
                              // For other types, allow alphanumeric
                              isValid = /^[A-Z0-9\s]{0,20}$/i.test(val);
                            }

                            if (isValid) {
                              // Convert to uppercase for PAN, Driving License, Passport, Voter ID
                              if (["pan_card", "driving_licence", "passport", "voter_id"].includes(idType)) {
                                onValueChange(section.id, "identification_number", val.toUpperCase());
                              } else {
                                onValueChange(section.id, "identification_number", val);
                              }
                              clearValidationError(section.id);
                            }
                          }}
                          placeholder={
                            section.values.identification_type === "aadhaar" ? "Enter 12-digit Aadhaar Number" :
                              section.values.identification_type === "pan_card" ? "Enter 10-character PAN (e.g., ABCDE1234F)" :
                                section.values.identification_type === "driving_licence" ? "Enter Driving License Number" :
                                  section.values.identification_type === "passport" ? "Enter 8-character Passport Number" :
                                    section.values.identification_type === "voter_id" ? "Enter 10-character Voter ID" :
                                      "Enter Identification Number"
                          }
                        />
                        {/* Real-time validation messages */}
                        {section.values.identification_type === "aadhaar" && section.values.identification_number && section.values.identification_number.length !== 12 && (
                          <div className="text-danger small mt-1">Aadhaar must be exactly 12 digits</div>
                        )}
                        {section.values.identification_type === "pan_card" && section.values.identification_number && section.values.identification_number.length !== 10 && (
                          <div className="text-danger small mt-1">PAN must be exactly 10 characters</div>
                        )}
                        {section.values.identification_type === "pan_card" && section.values.identification_number && section.values.identification_number.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(section.values.identification_number) && (
                          <div className="text-danger small mt-1">Invalid PAN format (e.g., ABCDE1234F)</div>
                        )}
                        {section.values.identification_type === "passport" && section.values.identification_number && section.values.identification_number.length !== 8 && (
                          <div className="text-danger small mt-1">Passport must be exactly 8 characters</div>
                        )}
                        {section.values.identification_type === "voter_id" && section.values.identification_number && section.values.identification_number.length !== 10 && (
                          <div className="text-danger small mt-1">Voter ID must be exactly 10 characters</div>
                        )}
                        {getFieldErrors(section.id, ["identification number", "identification_number"]).length > 0 && (
                          <div className="text-danger small mt-1">{getFieldErrors(section.id, ["identification number", "identification_number"]).join('. ')}</div>
                        )}
                      </Col>
                    </>
                  )}

                  {section.field !== "dob" && section.field !== "kit_size" && section.field !== "marital_status" &&
                    section.field !== "center_name" && section.field !== "center_type" && section.field !== "center_district" && !(section.field === "name" && isAthlete) && (
                      <Col md={12}>
                        <label className="form-label">
                          {section.field === "bank_details" ? "Upload Cancelled Cheque/Passbook" :
                            section.field === "sports_achievement" ? "Upload Sports Achievement" :
                              section.field === "sport" ? "Upload Sports Certificate" :
                                section.field === "educational_details" ? "Upload Education Certificate" :
                                  section.field === "center_details" ? "Upload Center Document" :
                                    "Upload Supportive Document"}
                          {" "}<span className="text-danger">*</span>
                        </label>

                        {(() => {
                          let displayFileUrl = section.values.fileUrl;
                          let displayUploading = section.values.uploading;
                          let displaySuccess = section.values.uploadSuccess;
                          let displayError = section.values.uploadError;
                          let subType = null;

                          if (section.field === "educational_details") {
                            subType = section.values.highest_qualification;
                            if (subType) {
                              const uploadState = section.values.education_uploads?.[subType];
                              if (uploadState) {
                                displayFileUrl = uploadState.doc_file || uploadState.fileUrl;
                                displayUploading = uploadState.uploading;
                                displaySuccess = uploadState.success;
                                displayError = uploadState.error;
                              } else {
                                displayFileUrl = null;
                                displayUploading = false;
                                displaySuccess = false;
                                displayError = false;
                              }
                            }
                          }

                          return (
                            <div style={{ position: 'relative' }}>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 8,
                                    border: "2px solid #E5E7EB",
                                    background: "#F9FAFB",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                  }}
                                >
                                  {displayFileUrl ? (
                                    <img
                                      src={displayFileUrl}
                                      alt="uploaded preview"
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <span className="text-muted"><i className="bi bi-image fs-2"></i></span>
                                  )}
                                </div>

                                {displayFileUrl && (
                                  <>
                                    <a
                                      href={displayFileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-outline-secondary btn-sm"
                                    >
                                      View File
                                    </a>
                                    <span className="text-muted small">Selected File</span>
                                  </>
                                )}

                                {!displayFileUrl && (
                                  <>
                                    <label className="btn btn-outline-primary btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                      Browse
                                      <input
                                        type="file"
                                        accept="image/png,image/jpg,image/jpeg"
                                        hidden
                                        onClick={(e) => { e.target.value = null; }}
                                        onChange={(e) => {
                                          let subType = null;
                                          if (section.field === "educational_details") {
                                            subType = section.values.highest_qualification;
                                          } else if (section.field === "identification") {
                                            subType = section.values.identification_type;
                                          }

                                          onFileChange(section.id, e.target.files?.[0] || null, subType);
                                          clearValidationError(section.id);
                                        }}
                                      />
                                    </label>
                                    <span className="text-muted small">
                                      {displayUploading ? "Uploading..." :
                                        displaySuccess ? "Uploaded Successfully" :
                                          displayError ? "Upload Failed" :
                                            "No File Chosen"}
                                    </span>
                                  </>
                                )}
                              </div>

                              {/* Delete icon - positioned absolutely */}
                              {displayFileUrl && (
                                <span
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "38px",
                                    color: "#EF4444",
                                    cursor: "pointer",
                                    fontSize: "15px",
                                    backgroundColor: "white",
                                    borderRadius: "40%",
                                    zIndex: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "15px",
                                    height: "15px",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                  }}
                                  onClick={() => handleRemoveFile(section.id, subType)}
                                  title="Remove file"
                                >
                                  <i className="bi bi-x-circle-fill"></i>
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        <div className="form-text mt-2">File Supported: PNG, JPG & JPEG max 2MB</div>
                        {section.field === "educational_details" ? (
                          getFieldErrors(section.id, ["Highest Qualification", "Education Certificate", "document", "file", "supportive"]).length > 0 && (
                            <div className="text-danger small mt-1">{getFieldErrors(section.id, ["Highest Qualification", "Education Certificate", "document", "file", "supportive"]).join('. ')}</div>
                          )
                        ) : (
                          getFieldErrors(section.id, ["document", "file", "supportive"]).length > 0 && (
                            <div className="text-danger small mt-1">{getFieldErrors(section.id, ["document", "file", "supportive"]).join('. ')}</div>
                          )
                        )}
                      </Col>
                    )}

                </Row>
              </div>
            );
          })}

          <div className="mb-3">
            <Button
              type="button"
              variant="light"
              className="d-flex w-25 add-more-btn text-start gap-1"
              onClick={handleAddSection}
            >
              <i className="bi bi-plus-square me-1"></i>Change More Details
            </Button>
          </div>

          <Button type="submit" className="gradient-btn w-100" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </form>
      </Card.Body>
    </Card >
  );
}